"use client";

import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Container,
  Stack,
  Paper,
  Snackbar,
  Alert,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import axios from "axios";

export default function ContactUs() {
  // ================= STATE =================
  const [settings, setSettings] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    msg: "",
    type: "success",
  });

  // ================= FETCH SETTINGS =================
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/setting`
        );

        setSettings(res.data.data);
      } catch (err) {
        console.error("Settings API error", err);
        setToast({
          open: true,
          msg: "Settings API fetch error",
          type: "error",
        });
      }
    };

    fetchSettings();
  }, []);

  // ================= FORM HANDLER =================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    // 🔥 REAL-TIME VALIDATION
    let error = "";

    if (name === "email") {
      if (!value) {
        error = "Email required";
      } else if (!value.includes("@")) {
        error = "Email must contain @";
      } else if (!/\S+@\S+\.\S+/.test(value)) {
        error = "Invalid email";
      }
    }

    if (name === "mobile") {
      if (!value) {
        error = "Mobile required";
      } else if (!/^[0-9]{10}$/.test(value)) {
        error = "Enter valid 10 digit number";
      }
    }

    if (name === "firstName" && !value) {
      error = "First name required";
    }

    if (name === "lastName" && !value) {
      error = "Last name required";
    }

    if (name === "message" && !value) {
      error = "Message required";
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const validate = () => {
    let temp = {};

    if (!form.firstName) temp.firstName = "First name required";
    if (!form.lastName) temp.lastName = "Last name required";

    if (!form.email) {
      temp.email = "Email required";
    } else if (!/\S+@\S+\.\S+/.test(form.email)) {
      temp.email = "Invalid email";
    }

    if (!form.mobile) {
      temp.mobile = "Mobile required";
    } else if (!/^[0-9]{10}$/.test(form.mobile)) {
      temp.mobile = "Enter valid 10 digit number";
    }

    if (!form.message) temp.message = "Message required";

    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  // ================= SUBMIT =================
  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setLoading(true);

      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/contactus`,
        form
      );

      setToast({
        open: true,
        msg: "Message sent successfully!",
        type: "success",
      });

      setForm({
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        message: "",
      });
    } catch (err) {
      setToast({
        open: true,
        msg: "Something went wrong!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ background: "#f3f4f6", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="xl">
        <Grid container spacing={4} alignItems="stretch">

          {/* MAP */}


          {/* MAP SECTION */}
          <Grid item xs={12} md={4} sx={{
            width: {
              xs: "100%",
              sm: "45%",
              md: "30%",
            },
          }}>
            <Box
              sx={{
                height: { xs: 250, md: "100%" },
                minHeight: 350,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: 2, // Thodu elevation api shakay
              }}
            >
              {/* Check karo ke settings load thaya chhe ane map_url available chhe */}
              {settings?.map_url ? (
                <iframe
                  title="Google Map Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  src={settings.map_url} // Aa URL Admin panel mathi aavshe
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Typography>Loading Map...</Typography>
                </Box>
              )}
            </Box>
          </Grid>



          {/* INFO */}
          <Grid item xs={12} md={4} sx={{
            width: {
              xs: "100%", // mobile
              sm: "45%", // tablet
              md: "30%", // desktop
            },
          }}>
            <Paper sx={{ p: 5, borderRadius: 4 }}>
              <Typography variant="h4" fontWeight="bold" mb={4}>
                Meet us
              </Typography>

              <Stack spacing={4}>
                {/* PHONE */}
                <Box display="flex">
                  <PhoneIcon sx={{ color: "primary.main", mr: 2 }} />
                  <Box>
                    <Typography fontWeight="bold">Phone</Typography>
                    <Typography
                      component="a"
                      href={`tel:+91${settings?.contactNumber}`}
                      sx={{
                        color: "primary.main",
                        textDecoration: "none",
                      }}
                    >
                      +91 {settings?.contactNumber}
                    </Typography>
                  </Box>
                </Box>

                {/* ADDRESS */}
                <Box display="flex">
                  <LocationOnIcon sx={{ color: "primary.main", mr: 2 }} />
                  <Box>
                    <Typography fontWeight="bold">Our Office</Typography>
                    <Typography>
                      {settings?.address || "Address not available"}
                    </Typography>
                  </Box>
                </Box>

                {/* EMAIL */}
                <Box display="flex">
                  <EmailIcon sx={{ color: "primary.main", mr: 2 }} />
                  <Box>
                    <Typography fontWeight="bold">Chat to us</Typography>
                    <Typography
                      component="a"
                      href={`mailto:${settings?.email}`}
                      sx={{
                        color: "primary.main",
                        textDecoration: "none",
                      }}
                    >
                      {settings?.email}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* FORM */}
          <Grid item xs={12} md={4} sx={{
            width: {
              xs: "100%", // mobile
              sm: "45%", // tablet
              md: "33%", // desktop
            },
          }}>
            <Paper sx={{ p: 5, borderRadius: 4 }}>
              <Typography variant="h4" fontWeight="bold" mb={4}>
                Get in Touch
              </Typography>

              <Stack spacing={3}>
                <TextField
                  label="First Name"
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />

                <TextField
                  label="Last Name"
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />

                <TextField
                  label="Email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                />

                <TextField
                  label="Mobile"
                  name="mobile"
                  value={form.mobile}
                  onChange={handleChange}
                  error={!!errors.mobile}
                  helperText={errors.mobile}
                />

                <TextField
                  label="Message"
                  name="message"
                  multiline
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={errors.message}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 3,
                  }}
                >
                  {loading ? "Submitting..." : "Submit"}
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.type} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}